import { saveAs } from 'file-saver';
import { DateTime } from 'luxon';

// Store tokens keyed by user email to prevent overwriting when multiple accounts are used in different tabs.
// Only the token for the current user is used (verified via store's userProfile).
const TOKEN_KEY_PREFIX = 'token_';
const LAST_USER_KEY = 'lastUser';
const ACTIVE_USER_KEY = 'activeUser';
const ACCOUNT_NAME_KEY_PREFIX = 'account_name_';

const readActiveUserOverride = (): string | null => {
  try {
    return sessionStorage.getItem(ACTIVE_USER_KEY);
  } catch {
    return null;
  }
};

/**
 * The account this tab acts as. sessionStorage is per tab, so each tab keeps its own user; a tab that never
 * picked one (fresh tab) falls back to the account that was used last in the browser. An empty string means
 * the tab deliberately has no active account (e.g. while adding another account on the login page).
 */
export const getActiveUser = (): string => {
  const override = readActiveUserOverride();
  return override !== null ? override : (localStorage.getItem(LAST_USER_KEY) || '');
};

/** Make `email` the account of this tab ('' = none, null = forget the choice) and the default for fresh tabs. */
export const setActiveUser = (email: string | null): void => {
  try {
    if (email === null) {
      sessionStorage.removeItem(ACTIVE_USER_KEY);
    } else {
      sessionStorage.setItem(ACTIVE_USER_KEY, email);
    }
  } catch {
    // sessionStorage unavailable: fall back to the shared lastUser
  }
  if (email) {
    localStorage.setItem(LAST_USER_KEY, email);
  }
};

/** Get token for a specific user, or for this tab's active user when restoring a session (no userEmail). */
export const getLocalToken = (userEmail?: string) => {
  const key = `${TOKEN_KEY_PREFIX}${userEmail || getActiveUser()}`;
  const token = localStorage.getItem(key);
  // Backward compat: if no scoped token, fall back to legacy 'token' key
  if (!token && !userEmail && readActiveUserOverride() === null) {
    return localStorage.getItem('token');
  }
  return token;
};

/** Save token for a user. Call after getMe() so we have the email. */
export const saveLocalToken = (token: string, userEmail: string) => {
  localStorage.setItem(`${TOKEN_KEY_PREFIX}${userEmail}`, token);
  localStorage.setItem(LAST_USER_KEY, userEmail);
};

/** Remove token for a user. Pass userEmail when logging out to remove the correct one. */
export const removeLocalToken = (userEmail?: string) => {
  const email = userEmail ?? getActiveUser();
  if (email) {
    localStorage.removeItem(`${TOKEN_KEY_PREFIX}${email}`);
    localStorage.removeItem(`${ACCOUNT_NAME_KEY_PREFIX}${email}`);
    if (localStorage.getItem(LAST_USER_KEY) === email) {
      localStorage.removeItem(LAST_USER_KEY);
    }
    if (readActiveUserOverride() === email) {
      setActiveUser(null);
    }
  }
  // Clear legacy token for backward compat
  localStorage.removeItem('token');
};

/** Full page navigation; used when switching accounts so no data of the previous user survives in memory. */
export const hardNavigate = (href: string): void => {
  window.location.assign(href);
};

export interface StoredAccount {
  email: string;
  fullName: string;
}

/** Remember the display name of an account so the account switcher can show it without an API call. */
export const saveLocalAccountName = (userEmail: string, fullName?: string | null): void => {
  if (fullName) {
    localStorage.setItem(`${ACCOUNT_NAME_KEY_PREFIX}${userEmail}`, fullName);
  }
};

/** All accounts that currently have a token stored in this browser, sorted by name. */
export const listStoredAccounts = (): StoredAccount[] => {
  const accounts: StoredAccount[] = [];
  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i);
    if (!key || !key.startsWith(TOKEN_KEY_PREFIX) || key.length === TOKEN_KEY_PREFIX.length) continue;
    const email = key.slice(TOKEN_KEY_PREFIX.length);
    accounts.push({ email, fullName: localStorage.getItem(`${ACCOUNT_NAME_KEY_PREFIX}${email}`) || email });
  }
  return accounts.sort((a, b) => a.fullName.localeCompare(b.fullName));
};

const REFRESH_TOKEN_KEY_PREFIX = 'refresh_token_';

export const getLocalRefreshToken = (userEmail?: string): string | null =>
  localStorage.getItem(`${REFRESH_TOKEN_KEY_PREFIX}${userEmail || getActiveUser()}`);

export const saveLocalRefreshToken = (token: string, userEmail: string): void => {
  localStorage.setItem(`${REFRESH_TOKEN_KEY_PREFIX}${userEmail}`, token);
};

export const removeLocalRefreshToken = (userEmail?: string): void => {
  const email = userEmail ?? getActiveUser();
  if (email) {
    localStorage.removeItem(`${REFRESH_TOKEN_KEY_PREFIX}${email}`);
  }
};

const LAST_ACTIVITY_KEY_PREFIX = 'last_activity_';

export const saveLastActivity = (userEmail: string): void => {
  localStorage.setItem(`${LAST_ACTIVITY_KEY_PREFIX}${userEmail}`, String(Date.now()));
};

export const getLastActivity = (userEmail?: string): number | null => {
  const email = userEmail ?? getActiveUser();
  if (!email) return null;
  const val = localStorage.getItem(`${LAST_ACTIVITY_KEY_PREFIX}${email}`);
  return val ? parseInt(val, 10) : null;
};

export const removeLastActivity = (userEmail?: string): void => {
  const email = userEmail ?? getActiveUser();
  if (email) localStorage.removeItem(`${LAST_ACTIVITY_KEY_PREFIX}${email}`);
};

export const formatTimestamp = (iso: string): string => DateTime.fromISO(iso).toFormat('yyyy-MM-dd HH:mm');

export const formatDateOnly = (iso: string): string => DateTime.fromISO(iso).toFormat('yyyy-MM-dd');

export const formatCurrency = (amount: number | undefined | null): string => {
  if (amount === undefined || amount === null) return '€0,00';
  return new Intl.NumberFormat('nl-BE', { style: 'currency', currency: 'EUR' }).format(amount);
};

export const isIsoDate = (value: string) => {
  try {
    (new Date(value)).toISOString();
    // Extra: check for ISO 8601 format
    if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) {
      return false;
    }
    return true;
  } catch {
    return false;
  }
};

export const debounce = <T extends (...args: any[]) => any>(
  fn: T,
  delay: number
): T & { cancel: () => void } => {
  let timeoutId: number | null = null;

  const debouncedFn = ((...args: Parameters<T>) => {
    if (timeoutId !== null) {
      clearTimeout(timeoutId);
    }

    timeoutId = window.setTimeout(() => {
      fn(...args);
      timeoutId = null;
    }, delay);
  }) as T & { cancel: () => void };

  debouncedFn.cancel = () => {
    if (timeoutId !== null) {
      clearTimeout(timeoutId);
      timeoutId = null;
    }
  };

  return debouncedFn;
};

/**
 * Fix mobile Safari bug (FileSaver.js) where erroneous extensions like .xml or .txt
 * get appended to PDFs, e.g. "invoice.pdf" becomes "invoice.pdf.xml". Only strips
 * when the bad suffix follows .pdf, so legitimate .xml/.txt files are preserved.
 */
function normalizeDownloadFilename(filename: string | undefined, fallback: string): string {
  const name = (filename && filename.trim()) || fallback;
  if (name.toLowerCase().endsWith('.pdf.xml')) return name.slice(0, -4); // remove .xml
  if (name.toLowerCase().endsWith('.pdf.txt')) return name.slice(0, -4); // remove .txt
  return name;
}

/**
 * Download a blob with a proper filename. Fixes mobile Safari issues where wrong
 * extensions (e.g. .pdf.xml) get appended. Only enforces PDF MIME type when the
 * filename extension is .pdf.
 */
export function downloadBlob(
  blob: Blob,
  filename: string | undefined,
  fallback = 'document'
): void {
  const safeFilename = normalizeDownloadFilename(filename, fallback);
  const blobToSave =
    safeFilename.toLowerCase().endsWith('.pdf')
      ? new Blob([blob], { type: 'application/pdf' })
      : blob;
  saveAs(blobToSave, safeFilename);
}